import { useAuth } from '@/lib/auth/auth';
import type { VenuePublicDeal } from '@/lib/api/apiTypes';
import { buildNearbyVenuesPath, roundMapCoordinate } from '@/features/map/mapQuery';
import { useQuery } from '@tanstack/react-query';

export interface NearbyVenueResult {
  id: string;
  name: string;
  address: string | null;
  lat: number;
  lng: number;
  category: string | null;
  is_verified: boolean;
  logo_url: string | null;
  distance_miles: number;
  upcoming_event_count: number;
  has_live_event: boolean;
  deals: VenuePublicDeal[];
}

interface NearbyVenuesResponse {
  venues: NearbyVenueResult[];
  total: number;
}

const nearbyVenueKeys = {
  all: ['nearby-venues'] as const,
  list: (params: {
    latitude: number;
    longitude: number;
    originLatitude: number;
    originLongitude: number;
    radiusMiles: number;
    category: string;
    limit: number;
  }) => [...nearbyVenueKeys.all, params] as const,
};

export function useNearbyVenues({
  latitude,
  longitude,
  originLatitude,
  originLongitude,
  radiusMiles,
  category,
  limit,
  enabled = true,
}: {
  latitude: number;
  longitude: number;
  originLatitude: number;
  originLongitude: number;
  radiusMiles: number;
  category: string;
  limit: number;
  enabled?: boolean;
}) {
  const request = useAuth((state) => state.request);
  const queryLatitude = roundMapCoordinate(latitude);
  const queryLongitude = roundMapCoordinate(longitude);
  const queryOriginLatitude = roundMapCoordinate(originLatitude);
  const queryOriginLongitude = roundMapCoordinate(originLongitude);

  return useQuery({
    queryKey: nearbyVenueKeys.list({
      latitude: queryLatitude,
      longitude: queryLongitude,
      originLatitude: queryOriginLatitude,
      originLongitude: queryOriginLongitude,
      radiusMiles,
      category,
      limit,
    }),
    enabled,
    placeholderData: (previousData) => previousData,
    queryFn: () =>
      request<NearbyVenuesResponse>(
        buildNearbyVenuesPath({
          latitude: queryLatitude,
          longitude: queryLongitude,
          originLatitude: queryOriginLatitude,
          originLongitude: queryOriginLongitude,
          radiusMiles,
          category,
          limit,
        })
      ),
    staleTime: 30_000,
  });
}
